import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import {
  getProfile, createProfile, updateProfile,
  getTaxInfo, saveTaxInfo, getLicense, saveLicense,
  listAddresses, createAddress, updateAddress,
  crossRecordProblems, sellsInternetService, upstreamProblems, upstreamToWrite, upstreamSaveErrors, invoiceIdentityFor,
  EMPTY_PROFILE, EMPTY_TAXINFO, EMPTY_LICENSE, EMPTY_ADDRESS, REGISTERED_ADDRESS_TYPE, findRegisteredAddress, EMPTY_UPSTREAM,
  type OperatorAddress,
  type OperatorProfile as Profile,
  type OperatorTaxInfo as TaxInfo,
  type OperatorLicense as License,
  type UpstreamDraft,
} from "../../api/operator";
import { isValidPan, isValidGstin, panFromGstin, normalizeNamespace } from "../../domain/operator";
import { PROVIDER, tierExplained, tierLabel } from "../../domain/labels";
import { PortalContactCard } from "./PortalContactCard";
import { PortalAddressCard } from "./PortalAddressCard";
import { DistributorCard } from "./DistributorCard";
import { OwnerTypeCard } from "./OwnerTypeCard";
import { VerifiedDetailsCard } from "./VerifiedDetailsCard";
import { getSetupStatus } from "../../api/operatorSetup";
import { ApprovalCard } from "./ApprovalCard";
import { ChangePasswordCard } from "./ChangePasswordCard";
import { InvoiceLogoCard } from "./InvoiceLogoCard";
import { canEditProviderIdentity, canReadInvoiceLogo, isStaffOnly } from "../../components/nav";

const notFound = (e: unknown) => e instanceof ApiError && e.kind === "not-found";

/**
 * The types an admin may set here. PIN and FRANCHISE come only from a parent's
 * kind and are shown read-only above. CLOUD_TENANT is listed so an existing
 * one displays as itself instead of an empty select.
 */
const PROFILE_TYPES: Profile["operatortype"][] = ["ISP", "VNO", "OPERATOR", "NETWORK_OPERATOR", "CLOUD_TENANT"];

/** Returns null when the record simply is not there yet, rethrows anything else. */
async function safeGet<T>(f: () => Promise<{ data: T }>): Promise<T | null> {
  try {
    const r = await f();
    return r.data ?? null;
  } catch (e) {
    if (notFound(e)) return null;
    throw e;
  }
}

export function OperatorProfileScreen() {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [target, setTarget] = useState(identity.namespace ?? "");
  const [ns, setNs] = useState(identity.namespace ?? "");
  // 🔑 Only superadmin may change a provider's legal identity (the writes are
  // radaccess operator:*). Everyone else who can read it sees it read-only
  // (owner decision 2026-09-19); the contact card above stays editable.
  const readOnly = !canEditProviderIdentity(identity.roles);
  // Staff hold no operatoridentity:read: their Edit profile is their own
  // password and nothing else (owner 2026-10-22).
  const staffOnly = isStaffOnly(identity.roles);
  // A self-onboarded operator's legal name, PAN and GSTIN change only by a new check.
  const setup = useQuery({ queryKey: ["setup", ns], enabled: Boolean(identity.token && ns) && !staffOnly, retry: false, queryFn: ({ signal }) => getSetupStatus(identity, ns, signal) });
  const selfOnboarded = setup.data?.selfonboarded === true;

  const [profile, setProfile] = useState<Profile | null>(null);
  const [tax, setTax] = useState<TaxInfo | null>(null);
  const [lic, setLic] = useState<License | null>(null);
  const [address, setAddress] = useState<OperatorAddress | null>(null);
  const [existing, setExisting] = useState({ profile: false, tax: false, lic: false, address: false });
  const [upstream, setUpstream] = useState<UpstreamDraft>(EMPTY_UPSTREAM);

  const load = useQuery({
    queryKey: ["operator", ns],
    enabled: Boolean(identity.token && ns) && !staffOnly,
    retry: false,
    queryFn: async () => {
      // Each record is fetched independently and a 404 means "not recorded
      // yet", not a failure — an operator being backfilled has none of them.
      const one = async <T,>(f: () => Promise<{ data: T }>, fallback: T) => {
        try {
          const r = await f();
          return { value: r.data ?? fallback, found: r.data !== null };
        } catch (e) {
          if (notFound(e)) return { value: fallback, found: false };
          throw e;
        }
      };
      const p = await one(() => getProfile(identity, ns), EMPTY_PROFILE(ns));
      const t = await one(() => getTaxInfo(identity, ns), EMPTY_TAXINFO(ns));
      const l = await one(() => getLicense(identity, ns), EMPTY_LICENSE(ns));
      const a = await one(() => listAddresses(identity, ns), [] as OperatorAddress[]);
      const registered = findRegisteredAddress(a.value);
      // A legacy "registered" row is SHOWN, then saved as a new canonical row;
      // only a canonical row can be updated in place, because the update is
      // keyed on the type.
      setAddress(registered ? { ...registered, namespace: ns, addresstype: REGISTERED_ADDRESS_TYPE } : EMPTY_ADDRESS(ns));
      setProfile({ ...p.value, namespace: ns });
      setTax({ ...t.value, namespace: ns });
      setLic({ ...l.value, namespace: ns });
      setExisting({ profile: p.found, tax: t.found, lic: l.found, address: registered?.addresstype === REGISTERED_ADDRESS_TYPE });
      setUpstream((u) => ({
        ...u,
        namespace: u.namespace || (p.value.upstreamispnamespace ?? "").trim(),
      }));
      return { p: p.found, t: t.found, l: l.found };
    },
  });

  const save = useMutation<string[], Error>({
    mutationFn: async () => {
      if (!profile || !tax || !lic) throw new ApiError("fail-closed", "Nothing loaded yet.");
      const done: string[] = [];

      // 🔑 THE UPSTREAM'S OWN RECORD IS WRITTEN FIRST, AND WRITTEN AT ITS OWN
      // NAMESPACE. radbilling resolves an unlicensed operator's invoice
      // supplier by walking UP the tree and reading THAT namespace's profile,
      // taxinfo and address -- so the details typed here have to land there or
      // nothing reads them. Writing it first also means the pointer this
      // operator is about to store never dangles.
      //
      // ⛔ This write is REFUSED 403 for a caller scoped below the upstream,
      // and that is correct: the record is another company's legal identity.
      // Only a provider that invoices internet service has an upstream to
      // write: a Network Operator or Cloud Tenant has none (sellsInternetService).
      const un = upstreamToWrite(lic.holdslicense, profile.operatortype, upstream);
      if (un) {
        const up = await safeGet(() => getProfile(identity, un));
        await (up
          ? updateProfile(identity, { ...up, namespace: un, legalname: upstream.legalname })
          : createProfile(identity, {
              namespace: un,
              legalname: upstream.legalname,
              operatortype: "ISP",
              allowpureinternetservice: true,
            }));
        done.push(`upstream profile (${un})`);

        const ut = await safeGet(() => getTaxInfo(identity, un));
        await saveTaxInfo(identity, {
          namespace: un,
          pan: upstream.pan.trim().toUpperCase(),
          gstin: upstream.gstin.trim().toUpperCase(),
          registrationstatus: upstream.registrationstatus,
          filingfrequency: "MONTHLY",
        }, ut !== null);
        done.push("upstream tax");

        const ul = await safeGet(() => getLicense(identity, un));
        await saveLicense(identity, {
          namespace: un,
          holdslicense: true,
          licensetype: upstream.licensetype,
          licensenumber: upstream.licensenumber,
          licensedentity: upstream.licensedentity || upstream.legalname,
          validfrom: upstream.validfrom ? `${upstream.validfrom}T00:00:00Z` : null,
          validto: upstream.validto ? `${upstream.validto}T00:00:00Z` : null,
          invoiceseriescode: "",
          // It holds the licence, so it invoices in its own name by
          // definition -- there is no upstream above it in this form.
          subscriberinvoiceidentity: "own",
        }, ul !== null);
        done.push("upstream licence");

        const addrs = (await safeGet(() => listAddresses(identity, un))) ?? [];
        const reg = { ...EMPTY_ADDRESS(un), address: upstream.address, city: upstream.city,
                      state: upstream.state, pin: upstream.pin };
        await (addrs.some((a) => a.addresstype === REGISTERED_ADDRESS_TYPE)
          ? updateAddress(identity, reg)
          : createAddress(identity, reg));
        done.push("upstream address");
      }

      // 🔑 Profile first among this operator's own three. taxinfo and license
      // are keyed on the namespace and mean nothing without the operator they
      // describe, and there is no transaction across them.
      await (existing.profile ? updateProfile(identity, profile) : createProfile(identity, profile));
      done.push("profile");
      await saveTaxInfo(identity, tax, existing.tax);
      done.push("taxinfo");
      // 🔑 Whose name goes on this operator's internet invoices is DERIVED
      // from whether it names an upstream, not asked as a separate question --
      // naming one IS saying "they invoice for me". See invoiceIdentityFor.
      await saveLicense(
        identity,
        { ...lic, subscriberinvoiceidentity: invoiceIdentityFor(profile) },
        existing.lic,
      );
      done.push("licence");
      // The provider's OWN registered address: printed on its invoices and the
      // place of supply. Written only when something is filled in, so an empty
      // form never creates a blank row.
      if (address && [address.address, address.city, address.state, address.pin].some((v) => v.trim())) {
        const reg = { ...address, namespace: ns, addresstype: REGISTERED_ADDRESS_TYPE };
        await (existing.address ? updateAddress(identity, reg) : createAddress(identity, reg));
        done.push("registered address");
      }
      return done;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["operator", ns] }),
  });

  const problems: string[] = [];
  // Not errors: what the profile still lacks before it can be APPROVED to
  // invoice internet service. Shown, never blocking Save.
  const approvalNeeds: string[] = [];
  if (profile && lic && tax) {
    if (!profile.legalname.trim()) problems.push("Legal name is required.");
    if ((profile.tradename ?? "").trim().length > 255) problems.push("Trade name must be at most 255 characters.");
    problems.push(...crossRecordProblems(profile, lic));
    const pan = tax.pan.trim().toUpperCase();
    const gstin = tax.gstin.trim().toUpperCase();
    if (!pan) problems.push("PAN is required.");
    else if (!isValidPan(pan)) problems.push("PAN is malformed.");
    if (gstin && !isValidGstin(gstin)) problems.push("GSTIN is malformed.");
    if (pan && gstin && isValidPan(pan) && isValidGstin(gstin) && panFromGstin(gstin) !== pan)
      problems.push(`The GSTIN embeds PAN ${panFromGstin(gstin)}, not ${pan}.`);
    if (tax.registrationstatus === "REGULAR" && !gstin)
      problems.push("A REGULAR registration requires a GSTIN.");
    if (tax.registrationstatus === "UNREGISTERED" && gstin)
      problems.push("An UNREGISTERED provider must not carry a GSTIN.");
    if (lic.holdslicense) {
      if (!lic.licensetype) problems.push("A licence type is required.");
      if (!lic.licensenumber.trim()) problems.push("A licence number is required.");
      if (!lic.licensedentity.trim()) problems.push("The entity named on the licence is required.");
      if (!lic.validfrom) problems.push("A licence valid-from date is required.");
    }
    if (lic.invoiceseriescode.length > 1)
      problems.push("The invoice series suffix must be at most one character (Rule 46).");
    // An unlicensed provider invoices internet service in its UPSTREAM's name,
    // under the upstream's GSTIN, so it may not be saved without one. But that
    // is only true of providers that invoice internet service at all: a Network
    // Operator sells management (SAC 9983xx) and a Cloud Tenant is billed on the
    // resources it consumes, and neither invoices a subscriber for internet.
    // Mirrors radaccess v1.80 (c815894), which applies the same predicate to
    // APPROVAL -- see sellsInternetService.
    if (!lic.holdslicense && sellsInternetService(profile.operatortype)) {
      // Only a WRONG upstream stops the save. A missing or incomplete one is
      // saved and reported below as what approval still needs -- radaccess
      // refuses approval without it (owner 2026-09-28, see upstreamSaveErrors).
      const wrong = upstreamSaveErrors(ns, upstream);
      problems.push(...wrong);
      approvalNeeds.push(...upstreamProblems(ns, upstream).filter((m) => !wrong.includes(m)));
    }
  }

  if (staffOnly) {
    return (
      <Box sx={{ maxWidth: 1100 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Edit profile</Typography>
        <ChangePasswordCard />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100 }}>
      {/* One label for every tier, with the tier as a neutral badge beside it.
          Naming the small ones something lesser than the big ones would
          reproduce the status problem that dropping "Operator" was meant to
          remove — and the tier still has to be visible, because it decides who
          may invoice pure internet service. */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5">{PROVIDER} profile</Typography>
        {profile && <Chip size="small" variant="outlined" label={tierLabel(profile.operatortype)} />}
      </Stack>

      {/* 🔑 THE NAMESPACE FIELD IS A CONVENIENCE, NOT THE BOUNDARY.
          Everywhere else in AnkhOS the tenant comes from the token and there
          is nothing to type, because the backend does not contain the request.
          Here it does: /access/operator is the ONE prefix where radaccess wires
          NamespaceIsolationMiddleware, so a namespace outside your own subtree
          is refused server-side with a 403 that this screen shows verbatim.
          Typing one you may not reach gets you a refusal, never data. */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>Acting as <code>{identity.namespace ?? "no namespace"}</code></AlertTitle>
        You may open your own namespace and anything beneath it. The gateway enforces that, not this
        form — a namespace outside your subtree is refused with a 403.
      </Alert>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small" label="Namespace" sx={{ minWidth: 320 }}
          value={target} onChange={(e) => setTarget(e.target.value)}
          helperText={
            target && normalizeNamespace(target) !== target
              ? `Will open ${normalizeNamespace(target)}`
              : " "
          }
        />
        <Button variant="outlined" sx={{ height: 40 }}
          onClick={() => { setNs(normalizeNamespace(target)); save.reset(); }}>
          Open
        </Button>
      </Stack>

      {/* 🔑 FIRST, AND LOADED ON ITS OWN. The contact is the operatorcontact
          resource, which an Operator may write; the records below are operator:*,
          superadmin only. A 403 on those must not hide the one card this role
          can use. */}
      {/* The signed-in person's own password, whatever namespace is open below. */}
      <ChangePasswordCard />
      {ns && <PortalContactCard ns={ns} />}
      {ns && <PortalAddressCard ns={ns} />}
      {ns && readOnly && identity.roles.some((r) => r.toLowerCase() === "operator") && <OwnerTypeCard ns={ns} />}
      {ns && selfOnboarded && <VerifiedDetailsCard ns={ns} />}
      {ns && <DistributorCard ns={ns} />}
      {ns && canReadInvoiceLogo(identity.roles) && <InvoiceLogoCard namespace={ns} />}
      {ns && !readOnly && <ApprovalCard namespace={ns} />}

      {load.isLoading && <Typography variant="body2">Loading…</Typography>}
      {load.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>
            {load.error instanceof ApiError && load.error.kind === "forbidden"
              ? "Refused — outside your subtree, or your role may not read provider records"
              : "Could not load"}
          </AlertTitle>
          {(load.error as Error).message}
        </Alert>
      )}

      {profile && tax && lic && !load.isLoading && !load.isError && (
        <>
          {readOnly && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Only an admin can edit these details</AlertTitle>
              Your legal name, PAN, GSTIN, GST status, registered address and licence are shown as
              recorded. To change any of them, contact Ankh support. You can edit your public contact
              details above.
            </Alert>
          )}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip size="small" label={existing.profile ? "profile: on file" : "profile: NEW"}
              color={existing.profile ? "default" : "warning"} />
            <Chip size="small" label={existing.tax ? "tax: on file" : "tax: NEW"}
              color={existing.tax ? "default" : "warning"} />
            <Chip size="small" label={existing.lic ? "licence: on file" : "licence: NEW"}
              color={existing.lic ? "default" : "warning"} />
          </Stack>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Divider textAlign="left"><Typography variant="caption">Identity</Typography></Divider>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField disabled={readOnly || selfOnboarded} fullWidth size="small" label="Legal name" value={profile.legalname}
                  onChange={(e) => setProfile({ ...profile, legalname: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField disabled={readOnly} fullWidth size="small" label="Trade / brand name (optional)"
                  value={profile.tradename ?? ""}
                  onChange={(e) => setProfile({ ...profile, tradename: e.target.value })}
                  helperText={tradeNameLine(profile.legalname, profile.tradename)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                {profile.operatortype === "PIN" || profile.operatortype === "FRANCHISE" ? (
                  // Set from the kind the parent chose; radaccess keeps it on save.
                  <TextField fullWidth size="small" label="Provider tier" value={tierLabel(profile.operatortype)} disabled
                    helperText="Set by your upstream provider" />
                ) : (
                  <TextField disabled={readOnly} select fullWidth size="small" label="Provider tier" value={profile.operatortype}
                    helperText={tierExplained(profile.operatortype)}
                    onChange={(e) => {
                      const operatortype = e.target.value as Profile["operatortype"];
                      // A type that does not sell internet has no upstream and may
                      // not invoice pure internet service; radaccess refuses the
                      // flag on it (400), so it is cleared rather than sent.
                      setProfile(sellsInternetService(operatortype)
                        ? { ...profile, operatortype }
                        : { ...profile, operatortype, upstreamispnamespace: "", allowpureinternetservice: false });
                    }}>
                    {PROFILE_TYPES.map((t) => <MenuItem key={t} value={t}>{tierLabel(t)}</MenuItem>)}
                  </TextField>
                )}
              </Grid>
              {sellsInternetService(profile.operatortype) ? (
                <>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="Upstream provider namespace"
                      helperText="Whose licence this provider's internet service is invoiced under"
                      value={profile.upstreamispnamespace ?? ""}
                      onChange={(e) => setProfile({ ...profile, upstreamispnamespace: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={<Checkbox disabled={readOnly} checked={profile.allowpureinternetservice}
                        onChange={(e) => setProfile({ ...profile, allowpureinternetservice: e.target.checked })} />}
                      label="May invoice pure internet service in its own name" />
                  </Grid>
                </>
              ) : (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info">
                    A {tierLabel(profile.operatortype)} does not sell internet service, so it has no upstream
                    provider and never invoices a subscriber for internet. The client supplies the internet
                    connection; the operator invoices the client for managing the network.
                  </Alert>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Divider textAlign="left"><Typography variant="caption">Tax</Typography></Divider>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField disabled={readOnly || selfOnboarded} fullWidth size="small" label="PAN (required)" value={tax.pan}
                  onChange={(e) => setTax({ ...tax, pan: e.target.value.toUpperCase() })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField disabled={readOnly || selfOnboarded} fullWidth size="small" label="GSTIN (if registered)" value={tax.gstin}
                  helperText="Must embed the PAN"
                  onChange={(e) => setTax({ ...tax, gstin: e.target.value.toUpperCase() })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField disabled={readOnly} select fullWidth size="small" label="Registration status" value={tax.registrationstatus}
                  onChange={(e) => setTax({ ...tax, registrationstatus: e.target.value as TaxInfo["registrationstatus"] })}>
                  {["REGULAR", "COMPOSITION", "UNREGISTERED"].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField disabled={readOnly} select fullWidth size="small" label="Filing frequency" value={tax.filingfrequency}
                  onChange={(e) => setTax({ ...tax, filingfrequency: e.target.value as TaxInfo["filingfrequency"] })}>
                  {["MONTHLY", "QUARTERLY"].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider textAlign="left"><Typography variant="caption">Licence</Typography></Divider>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={<Checkbox disabled={readOnly} checked={lic.holdslicense}
                    onChange={(e) => setLic(e.target.checked
                      ? { ...lic, holdslicense: true }
                      // Clearing the flag must clear the detail: radaccess
                      // rejects an unlicensed row that still carries a number,
                      // and a stale licence number reads as evidence.
                      : { ...EMPTY_LICENSE(lic.namespace), invoiceseriescode: lic.invoiceseriescode })} />}
                  label="Holds its own telecom or ISP licence" />
              </Grid>
              {lic.holdslicense && (
                <>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField disabled={readOnly} select fullWidth size="small" label="Type" value={lic.licensetype}
                      onChange={(e) => setLic({ ...lic, licensetype: e.target.value as License["licensetype"] })}>
                      {["UL", "ISP_A", "ISP_B", "ISP_C", "VNO"].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="Licence number" value={lic.licensenumber}
                      onChange={(e) => setLic({ ...lic, licensenumber: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="Entity on the licence" value={lic.licensedentity}
                      helperText="May differ from the legal name"
                      onChange={(e) => setLic({ ...lic, licensedentity: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField disabled={readOnly} fullWidth size="small" type="date" label="Valid from"
                      slotProps={{ inputLabel: { shrink: true } }}
                      value={(lic.validfrom ?? "").slice(0, 10)}
                      onChange={(e) => setLic({ ...lic, validfrom: e.target.value ? `${e.target.value}T00:00:00Z` : null })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField disabled={readOnly} fullWidth size="small" type="date" label="Valid to"
                      slotProps={{ inputLabel: { shrink: true } }}
                      value={(lic.validto ?? "").slice(0, 10)}
                      onChange={(e) => setLic({ ...lic, validto: e.target.value ? `${e.target.value}T00:00:00Z` : null })} />
                  </Grid>
                </>
              )}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField disabled={readOnly} fullWidth size="small" label="Invoice series suffix"
                  helperText="At most one character — Rule 46 caps the number at 16"
                  value={lic.invoiceseriescode}
                  onChange={(e) => setLic({ ...lic, invoiceseriescode: e.target.value })} />
              </Grid>

              {/* 🔑 SHOWN ONLY WHEN THIS OPERATOR HOLDS NO LICENCE, because
                  that is exactly when it cannot invoice internet service in
                  its own name and someone else must. The details go to the
                  UPSTREAM's own operator record, not onto this one -- see
                  UpstreamDraft in api/operator.ts for why. */}
              {!lic.holdslicense && sellsInternetService(profile.operatortype) && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Divider textAlign="left">
                      <Typography variant="caption">Upstream provider — invoices this provider&apos;s internet service</Typography>
                    </Divider>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="info" sx={{ mb: 1 }}>
                      These details are saved as the upstream&apos;s <strong>own</strong> provider
                      record at its namespace, not copied onto this one — so one company has one
                      GSTIN in one place, however many resellers sit beneath it. It must be an
                      ancestor namespace, and you must be scoped at or above it to write it.
                    </Alert>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="Upstream namespace"
                      helperText="e.g. net.supersonicbb"
                      value={upstream.namespace}
                      onChange={(e) => {
                        const v = e.target.value;
                        setUpstream({ ...upstream, namespace: v });
                        setProfile({ ...profile, upstreamispnamespace: v.trim().toLowerCase() });
                      }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="Upstream legal name"
                      helperText="Prints as the supplier on the invoice"
                      value={upstream.legalname}
                      onChange={(e) => setUpstream({ ...upstream, legalname: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="Upstream PAN" value={upstream.pan}
                      onChange={(e) => setUpstream({ ...upstream, pan: e.target.value.toUpperCase() })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="Upstream GSTIN" value={upstream.gstin}
                      helperText="Optional — an upstream may be composition"
                      onChange={(e) => setUpstream({ ...upstream, gstin: e.target.value.toUpperCase() })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField disabled={readOnly} select fullWidth size="small" label="Upstream licence type"
                      value={upstream.licensetype}
                      onChange={(e) => setUpstream({ ...upstream, licensetype: e.target.value as UpstreamDraft["licensetype"] })}>
                      {["UL", "ISP_A", "ISP_B", "ISP_C", "VNO"].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="Upstream licence number"
                      value={upstream.licensenumber}
                      onChange={(e) => setUpstream({ ...upstream, licensenumber: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="Entity on the upstream licence"
                      helperText="Defaults to the legal name"
                      value={upstream.licensedentity}
                      onChange={(e) => setUpstream({ ...upstream, licensedentity: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField disabled={readOnly} fullWidth size="small" type="date" label="Licence from"
                      slotProps={{ inputLabel: { shrink: true } }} value={upstream.validfrom}
                      onChange={(e) => setUpstream({ ...upstream, validfrom: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField disabled={readOnly} fullWidth size="small" type="date" label="Licence to"
                      slotProps={{ inputLabel: { shrink: true } }} value={upstream.validto}
                      onChange={(e) => setUpstream({ ...upstream, validto: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="Upstream registered address"
                      value={upstream.address}
                      onChange={(e) => setUpstream({ ...upstream, address: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="City" value={upstream.city}
                      onChange={(e) => setUpstream({ ...upstream, city: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="State"
                      helperText="Place of supply" value={upstream.state}
                      onChange={(e) => setUpstream({ ...upstream, state: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="PIN" value={upstream.pin}
                      onChange={(e) => setUpstream({ ...upstream, pin: e.target.value })} />
                  </Grid>
                </>
              )}

              {address && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Divider textAlign="left">
                      <Typography variant="caption">Registered address — printed on this provider&apos;s invoices</Typography>
                    </Divider>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="Registered address" value={address.address}
                      onChange={(e) => setAddress({ ...address, address: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="City" value={address.city}
                      onChange={(e) => setAddress({ ...address, city: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="State" helperText="Place of supply" value={address.state}
                      onChange={(e) => setAddress({ ...address, state: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField disabled={readOnly} fullWidth size="small" label="PIN" value={address.pin}
                      onChange={(e) => setAddress({ ...address, pin: e.target.value })} />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          {!readOnly && problems.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                {problems.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </Alert>
          )}

          {!readOnly && approvalNeeds.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="approval-needs">
              <AlertTitle>You can save this now. It cannot be approved yet.</AlertTitle>
              A provider without its own licence invoices internet service in its
              upstream&apos;s name, so it is not approved until its upstream is on file:
              <ul style={{ margin: "0.5em 0 0", paddingLeft: "1.2em" }}>
                {approvalNeeds.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </Alert>
          )}

          {save.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Save failed — some records may already be written</AlertTitle>
              The three records are saved in sequence with no transaction between them. If this
              failed partway, re-open the namespace to see what is on file before retrying.
              <br /><br />
              {save.error.message}
            </Alert>
          )}
          {save.isSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>Saved: {save.data.join(", ")}.</Alert>
          )}

          {!readOnly && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="contained" disabled={problems.length > 0 || save.isPending}
                onClick={() => save.mutate()}>
                {save.isPending ? "Saving…" : "Save provider"}
              </Button>
              {problems.length > 0 && !save.isPending && (
                <Typography variant="body2" color="warning.main" data-testid="save-blocked-reason">
                  {saveBlockedReason(problems)}
                </Typography>
              )}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}

/** What invoices print for this name, or the hint when there is no trade name. */
export function tradeNameLine(legal: string, trade: string | undefined): string {
  const t = (trade ?? "").trim();
  if (!t) return "Optional. Use only a trade name on your GST registration.";
  return `Invoices show: ${legal.trim() || "<legal name>"} (trading as ${t})`;
}

/** Why Save is greyed: the first thing to fix, so the reason is next to the button. */
export function saveBlockedReason(problems: string[]): string {
  const first = problems[0] ?? "";
  return problems.length === 1
    ? `Save is off until this is fixed: ${first}`
    : `Save is off until ${problems.length} things are fixed (listed above), first: ${first}`;
}
